import React, { useMemo, useRef, useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { startOfDay } from 'date-fns';
import { formatAbsoluteDateTime } from '@/lib/date-utils';
import { TimePicker } from './TimePicker';

export interface DatePickerButtonProps {
  selectedDate: Date | undefined;
  onDateSelect: (date: Date | undefined) => void;
  /**
   * Time-only changes route here when provided, so the parent can stage the
   * time without committing. Falls back to onDateSelect when omitted.
   */
  onTimeChange?: (date: Date) => void;
  placeholder?: string;
  className?: string;
  showTime?: boolean;
  /**
   * Range start endpoint to indicate (From). Rendered in its role color
   * independently of selectedDate. Same day as rangeEnd → To wins.
   */
  rangeStart?: Date;
  /** Range end endpoint to indicate (To). Rendered in its role color. */
  rangeEnd?: Date;
  /**
   * Controlled open state. When provided together with onOpenChange the parent
   * owns the popover; when omitted the picker manages its own open state.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Forwarded to the trigger Button so the parent can focus it later. */
  triggerRef?: React.Ref<HTMLButtonElement>;
  /** On a day-selection close, focus moves here instead of back to the trigger. */
  focusOnCloseRef?: React.RefObject<HTMLElement | null>;
}

/**
 * DatePickerButton - Reusable date picker component
 * Displays a button that opens a popover with a calendar for date selection
 * Optionally includes time selection
 */
export const DatePickerButton: React.FC<DatePickerButtonProps> = ({
  selectedDate,
  onDateSelect,
  onTimeChange,
  placeholder = 'Select date',
  className,
  showTime = false,
  rangeStart,
  rangeEnd,
  open,
  onOpenChange,
  triggerRef,
  focusOnCloseRef,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const popoverOpen = isControlled ? open : internalOpen;
  const setPopoverOpen = onOpenChange ?? setInternalOpen;

  const daySelectedRef = useRef(false);

  const handleDateSelect = (date: Date | undefined) => {
    daySelectedRef.current = false;

    if (!date) {
      // react-day-picker emits undefined on a same-day toggle-off; re-emit so the selection still stages.
      if (selectedDate) {
        daySelectedRef.current = true;
        onDateSelect(selectedDate);
        if (!isControlled) setInternalOpen(false);
      }
      return;
    }

    daySelectedRef.current = true;
    if (showTime && selectedDate) {
      // Keep the staged time when only the day changes.
      const newDate = new Date(date);
      newDate.setHours(selectedDate.getHours());
      newDate.setMinutes(selectedDate.getMinutes());
      newDate.setSeconds(selectedDate.getSeconds());
      onDateSelect(newDate);
    } else {
      onDateSelect(date);
    }

    // Uncontrolled self-closes on a day-click; controlled defers to the parent.
    if (!isControlled) setInternalOpen(false);
  };

  // Time edits route to onTimeChange; day-clicks route to onDateSelect.
  const emitTimeChange = onTimeChange ?? onDateSelect;
  const handleTimeChange = (newDate: Date) => {
    if (!selectedDate) {
      emitTimeChange(newDate);
      return;
    }
    const updatedDate = new Date(selectedDate);
    updatedDate.setHours(newDate.getHours());
    updatedDate.setMinutes(newDate.getMinutes());
    updatedDate.setSeconds(newDate.getSeconds());
    emitTimeChange(updatedDate);
  };

  // Indicate both range endpoints by role, normalized to start of day. When
  // From and To fall on the same day, To (blue) wins so only one cell renders.
  const calendarModifiers = useMemo(() => {
    const modifiers: { rangeStart?: Date[]; rangeEnd?: Date[] } = {};
    const normalizedEnd = rangeEnd ? startOfDay(rangeEnd) : undefined;
    if (normalizedEnd) modifiers.rangeEnd = [normalizedEnd];
    if (rangeStart) {
      const normalizedStart = startOfDay(rangeStart);
      if (
        !normalizedEnd ||
        normalizedStart.getTime() !== normalizedEnd.getTime()
      ) {
        modifiers.rangeStart = [normalizedStart];
      }
    }
    return modifiers.rangeStart || modifiers.rangeEnd ? modifiers : undefined;
  }, [rangeStart, rangeEnd]);

  const handleCloseAutoFocus = (event: Event): void => {
    if (daySelectedRef.current && focusOnCloseRef?.current) {
      event.preventDefault();
      focusOnCloseRef.current.focus();
    }
    daySelectedRef.current = false;
  };

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal',
            !selectedDate && 'text-muted-foreground',
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selectedDate ? formatAbsoluteDateTime(selectedDate) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        onCloseAutoFocus={handleCloseAutoFocus}
      >
        <CalendarComponent
          mode="single"
          selected={selectedDate}
          onSelect={handleDateSelect}
          autoFocus
          modifiers={calendarModifiers}
          // Lighten today only in range mode, to distinguish it from the To marker.
          classNames={
            calendarModifiers
              ? {
                  today:
                    'bg-accent-hover text-accent-hover-foreground rounded-md data-[selected=true]:rounded-none',
                }
              : undefined
          }
        />
        {showTime && (
          <div className="border-t p-3">
            <TimePicker
              date={selectedDate || new Date()}
              onChange={handleTimeChange}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
